import React, { useState, useEffect, useContext } from "react";
import { Typography, Modal, Spin } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const { Title } = Typography;

export default function TotalEmployees({ organisationId }) {
  const { authData, backUrl } = useContext(AuthContext);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgDataMap, setOrgDataMap] = useState({});

  const COLORS = ["#0e497b", "#ec4899"]; // BEMSS Navy Blue & Pink

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const params = {};
        if (organisationId && organisationId !== "ALL") {
          params.organisation_id = organisationId;
        }
        const res = await axios.get(`${backUrl}/api/emp/employees/dashboard-summary/`, {
          params,
          headers: { Authorization: `Bearer ${authData?.access}` },
        });

        const orgList = res.data?.organisation_distribution || [];
        const mapObj = {};
        const formatted = orgList.map((item) => {
          mapObj[item.organisation] = { male: item.Male, female: item.Female };
          return {
            organisation: item.organisation,
            Male: item.Male,
            Female: item.Female,
          };
        });

        setChartData(formatted);
        setOrgDataMap(mapObj);
      } catch (err) {
        console.error("Error fetching employee gender distribution:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [organisationId, backUrl, authData?.access]);

  const handleBarClick = (data) => {
    if (data && data.activeLabel) {
      setSelectedOrg(data.activeLabel);
      setIsModalOpen(true);
    }
  };

  const selectedOrgData =
    selectedOrg && orgDataMap[selectedOrg]
      ? [
          { name: "Male", value: orgDataMap[selectedOrg].male },
          { name: "Female", value: orgDataMap[selectedOrg].female },
        ]
      : [];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin size="medium" />
        <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>Loading Gender Summary...</div>
      </div>
    );
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={230}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          onClick={handleBarClick}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="organisation" tick={{ fill: "#64748b", fontSize: 12 }} />
          <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
          <Legend />
          <Bar dataKey="Male" stackId="a" fill="#0e497b" radius={[0, 0, 4, 4]} />
          <Bar dataKey="Female" stackId="a" fill="#ec4899" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <center style={{ marginTop: 8 }}>
        <Title level={5} style={{ color: "#0f172a", margin: 0, fontWeight: 700 }}>
          Employee Gender Distribution
        </Title>
      </center>

      <Modal
        title={`Gender Ratio - ${selectedOrg}`}
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        centered
        width={400}
      >
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={selectedOrgData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={90}
              label={(entry) => `${entry.name}: ${entry.value}`}
            >
              {selectedOrgData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Modal>
    </>
  );
}
